import { useEffect, useMemo, useState } from 'react';
import {
  datesForTourType,
  fetchTourTimes,
  leadRouterForPms,
  timesForDate,
  tourTypesFromDays,
  type TourTimesResponse,
} from './sources/leadsTourTimes';

/**
 * Loads tour availability from GET /tour-times and derives tour types, calendar
 * dates, and time slots in the same shape as useTourSchedulerData.
 */
export function useLeadsTourTimes({
  enabled,
  propertyId,
  selectedTourType,
  selectedDate,
}: {
  enabled: boolean;
  propertyId: string | null | undefined;
  selectedTourType: string | null;
  selectedDate: string | null;
}): {
  pms: string | null;
  leadRouter: 'RENT_CAFE' | 'ENTRATA' | 'KNOCK' | null;
  tourTypes: string[];
  availableDates: string[];
  timeSlots: string[];
  isInitializing: boolean;
  availabilityLoading: boolean;
  timeSlotsLoading: boolean;
} {
  const [payload, setPayload] = useState<TourTimesResponse | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    setPayload(null);
    if (!enabled || !propertyId) {
      setIsInitializing(false);
      return;
    }

    let cancelled = false;
    setIsInitializing(true);

    fetchTourTimes(propertyId).then((data) => {
      if (cancelled) return;
      setPayload(data);
      setIsInitializing(false);
    });

    return () => { cancelled = true; };
  }, [enabled, propertyId]);

  const days = payload?.days;
  const tourTypes = useMemo(() => tourTypesFromDays(days ?? []), [days]);
  const availableDates = useMemo(
    () => datesForTourType(days ?? [], selectedTourType),
    [days, selectedTourType],
  );
  const timeSlots = useMemo(
    () => timesForDate(days ?? [], selectedDate ?? '', selectedTourType),
    [days, selectedDate, selectedTourType],
  );

  return {
    pms: payload?.pms ?? null,
    leadRouter: leadRouterForPms(payload?.pms),
    tourTypes,
    availableDates,
    timeSlots,
    isInitializing,
    availabilityLoading: false,
    timeSlotsLoading: false,
  };
}
